import { Link } from 'react-router-dom';
import { benefits, useCases, comparisons, comparisonTools, faqs, securityPractices } from '@/mocks/categories';
import { useState } from 'react';

function WhatIsSection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            What Is VigorBuy Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto">
            <strong>VigorBuy Spreadsheet</strong> is a powerful spreadsheet solution designed to help users
            organize information, improve workflow efficiency, manage data, and simplify daily operations.
            Whether you&apos;re a small business owner tracking inventory, an online seller managing product listings,
            or a team leader coordinating projects, VigorBuy Spreadsheet provides the tools you need to work
            smarter and more efficiently.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-question-answer-line', title: 'What It Does', desc: 'Organizes data, manages workflows, automates tasks, and generates insightful reports all from one intuitive platform.' },
            { icon: 'ri-user-heart-line', title: 'Who Needs It', desc: 'Small businesses, online sellers, marketing teams, data analysts, project managers, and students seeking better productivity.' },
            { icon: 'ri-search-eye-line', title: 'Why People Search It', desc: 'Growing demand for efficient data management solutions that combine power with simplicity for everyday business needs.' },
            { icon: 'ri-star-line', title: 'Key Advantages', desc: 'Extensive template library, advanced automation, intuitive design, and enterprise-grade security at an accessible level.' },
          ].map((item) => (
            <div key={item.title} className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                <i className={`${item.icon} text-accent-600 text-lg`}></i>
              </div>
              <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2">{item.title}</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  return (
    <section id="benefits" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Why Choose VigorBuy Spreadsheet?
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Discover the key advantages that make VigorBuy Spreadsheet the smart choice for your data management needs
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60 hover:border-primary-200 transition-colors group">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                <i className={`${benefit.icon} text-primary-600 text-xl`}></i>
              </div>
              <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonTable() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Feature Comparison
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            See how VigorBuy Spreadsheet stacks up against traditional spreadsheet tools
          </p>
        </div>

        <div className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
          <div className="grid grid-cols-3 bg-background-200/50 px-5 py-3.5">
            <div className="text-xs font-semibold text-foreground-700">Feature</div>
            <div className="text-xs font-semibold text-primary-600">VigorBuy Spreadsheet</div>
            <div className="text-xs font-semibold text-foreground-500">Traditional Tools</div>
          </div>
          {comparisons.map((row, idx) => (
            <div
              key={row.feature}
              className={`grid grid-cols-3 px-5 py-3.5 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}
            >
              <div className="text-sm text-foreground-800 font-medium">{row.feature}</div>
              <div className="text-sm text-primary-600 flex items-center gap-1.5">
                <i className="ri-check-line text-accent-500 text-sm"></i>
                {row.vigorbuy}
              </div>
              <div className="text-sm text-foreground-500 flex items-center gap-1.5">
                <i className="ri-subtract-line text-foreground-400 text-xs"></i>
                {row.traditional}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCasesSection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            VigorBuy Spreadsheet Use Cases
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            From small business to enterprise, VigorBuy Spreadsheet adapts to your unique needs
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {useCases.map((item) => (
            <div key={item.title} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60 flex gap-4">
              <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-accent-600 text-lg`}></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SecuritySection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-check-line text-accent-600 text-2xl"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Security &amp; Trust
          </h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Your data security matters. Here&apos;s how VigorBuy Spreadsheet protects your information.
          </p>
        </div>

        <h3 className="font-heading text-lg font-semibold text-foreground-900 text-center mb-6">
          Is VigorBuy Spreadsheet Safe?
        </h3>

        <div className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
          <div className="grid grid-cols-2 bg-background-200/50 px-5 py-3.5">
            <div className="text-xs font-semibold text-foreground-700">Security Area</div>
            <div className="text-xs font-semibold text-foreground-700">Protection Method</div>
          </div>
          {securityPractices.map((item, idx) => (
            <div
              key={item.area}
              className={`grid grid-cols-2 px-5 py-3.5 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}
            >
              <div className="text-sm text-foreground-800 font-medium">{item.area}</div>
              <div className="text-sm text-foreground-600">{item.method}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonHub() {
  const toolLinks: Record<string, string> = {
    "vs-excel": "/article/vigorbuy-spreadsheet-vs-excel",
    "vs-google-sheets": "/article/vigorbuy-spreadsheet-vs-google-sheets",
    "vs-airtable": "/article/vigorbuy-spreadsheet-alternatives",
    "vs-notion": "/article/vigorbuy-spreadsheet-alternatives",
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
          Compare VigorBuy Spreadsheet
        </h2>
        <p className="text-sm text-foreground-600 mb-10">
          See how VigorBuy Spreadsheet compares to popular alternatives
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {comparisonTools.map((tool) => (
            <Link
              key={tool.slug}
              to={toolLinks[tool.slug] || '/article/what-is-vigorbuy-spreadsheet'}
              className="bg-background-50 rounded-xl p-6 border border-background-200/60 hover:border-primary-200 transition-colors group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-secondary-200 transition-colors">
                <i className="ri-scales-3-line text-secondary-600 text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-sm text-foreground-900">
                VigorBuy Spreadsheet vs {tool.name}
              </h3>
              <p className="text-xs text-foreground-500 mt-1">Read full comparison</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section id="faq" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-foreground-600">
            Everything you need to know about VigorBuy Spreadsheet
          </p>
        </div>

        <div className="space-y-2">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden"
            >
              <button
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-background-200/50 transition-colors"
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${openIdx === idx ? 'bg-primary-100 text-primary-600' : 'bg-background-200 text-foreground-400'}`}>
                  <i className={`text-sm transition-transform ${openIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                </div>
              </button>
              {openIdx === idx && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-primary-500">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
          Ready to Boost Your Productivity?
        </h2>
        <p className="text-sm md:text-base text-primary-100 mb-8 max-w-xl mx-auto">
          Discover the full power of VigorBuy Spreadsheet. Explore templates, features, and solutions designed
          to transform how you work with data.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 bg-white text-primary-600 font-semibold text-sm rounded-full hover:bg-primary-50 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            VigorBuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 bg-primary-600 text-white font-semibold text-sm rounded-full hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>
      </div>
    </section>
  );
}

export { WhatIsSection, BenefitsSection, ComparisonTable, UseCasesSection, SecuritySection, ComparisonHub, FAQSection, CTASection };